import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, CircularProgress } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import ColorBox from 'common/Box/ColorWhiteBox'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'
import {
  AllCampaignPromocodes,
  CampaignCodeTableRows,
  CampaignPromocodeDetails
} from 'types/api/faber/reward.type'

import { SearchToolbar } from '../../../Customers/vendor/Faber/otpackComponents/OtpToolbar'
import AddBenefitDialog from '../add_promo_code/AddBenefitDialog'
import AddCampaignPromoDialog from '../add_promo_code/AddCampaignPromoDialog'
import AddRewardProductDialog from '../add_promo_code/AddRewardProductDialog'

export default function CampaignRewards() {
  const role = OrbitSession.get('role')

  const [rows, setRows] = useState<CampaignPromocodeDetails[]>([])

  const [tempSearchValue, setTempSearchValue] = useState<string>('')

  const [sortDate] = useState<string>('id DESC')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Promocode')

  const [pageOffset, setPageOffset] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)

  const [rowCount, setRowCount] = useState(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Promocode']

  const queryOptions = useMemo(() => {
    return [searchValue, pageOffset, pageSize]
  }, [searchValue, pageOffset, pageSize])

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPageOffset(tmpOffset)
    setPage(newPage)
  }

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }

  // fetch campaign promocodes
  const {
    data: getCampaignPromocodes,
    isLoading: isGetCampaignPromocodesLoading
  } = useQuery(
    ['getCampaignPromocodes', faberToken, ...queryOptions],
    async (): Promise<AllCampaignPromocodes> => {
      const allPlans = await RewardsAPI.getCampaignPromocodes(
        searchValue,
        pageSize, //limit
        pageOffset, //offset
        sortDate,
        faberToken
      )
      return allPlans
    },
    {
      onError: () => {
        toast.error('Error fetching Campaign Promocodes')
        setRows([])
        setIsLoading(false)
      }
    }
  )

  useEffect(() => {
    if (getCampaignPromocodes) {
      const { data, total } = getCampaignPromocodes.body
      const allCampaignCodes: CampaignCodeTableRows[] = data.map(
        ({
          _id,
          createdAt,
          updatedAt,
          camapignRewardName,
          campaignSummary,
          campaignCode,
          campaignCodePrefix,
          rewardProducId,
          maxClaims,
          maxConsumption,
          validityStartAt = 0,
          validityEndAt = 0,
          isExpired,
          totalConsumed
        }) => ({
          _id,
          createdAt,
          updatedAt,
          camapignRewardName,
          campaignSummary,
          campaignCode,
          campaignCodePrefix,
          rewardProducId,
          maxClaims,
          maxConsumption,
          validFrom: new Date(validityStartAt).toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'Asia/Singapore'
          }),
          expiryAt: new Date(validityEndAt).toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'Asia/Singapore'
          }),
          isExpired,
          totalConsumed
        })
      )
      setRowCount(total)
      setRows(allCampaignCodes)
      setIsLoading(false)
    }
  }, [getCampaignPromocodes])

  //search values
  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempSearchValue(e.target.value)
  }

  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
    resetPage()
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  const columns: GridColDef[] = [
    {
      field: 'campaignCode',
      headerName: 'Promocode',
      flex: 0.2,
      minWidth: 160,
      renderCell: (params: { row: CampaignPromocodeDetails }) => {
        return <NormalText>{params.row.campaignCode}</NormalText>
      }
    },
    {
      field: 'campaignSummary',
      headerName: 'Description',
      flex: 1.4,
      minWidth: 100
    },

    {
      field: 'totalConsumed',
      headerName: 'Consumed',
      flex: 0.2,
      minWidth: 100
    },
    {
      field: 'maxConsumption',
      headerName: 'Max Available',
      flex: 0.2,
      minWidth: 100
    },
    {
      field: 'isExpired',
      headerName: 'Expired',
      flex: 0.2,
      minWidth: 70
    },
    {
      field: 'validFrom',
      headerName: 'Valid From',
      flex: 0.5,
      minWidth: 140
    },
    {
      field: 'expiryAt',
      headerName: 'Expiry At',
      flex: 0.5,
      minWidth: 140
    }
  ]

  return !isLoading ? (
    role === 'opsowner' ? (
      <Box>
        <ColorBox>
          <Box>
            <NormalText isBold>
              Please type promocode details in the search bar to begin
            </NormalText>
            <Box display='flex' flexDirection='row' gap={2}>
              <AddCampaignPromoDialog />
              <AddRewardProductDialog />
              <AddBenefitDialog />
            </Box>
            <MUIDataGrid
              getRowId={(row) => `${row.iccid}-${rows.indexOf(row)}`}
              tableHeight='750px'
              rows={rows}
              pagination
              columns={columns}
              paginationMode='server'
              rowCount={rowCount}
              page={page}
              loading={isGetCampaignPromocodesLoading}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              disableColumnMenu={true}
              sortingMode='server'
              sortingOrder={['desc', 'asc']}
              components={{
                Toolbar: SearchToolbar
              }}
              componentsProps={{
                toolbar: {
                  searchBar: 'server',
                  serverSearchBarProps: {
                    onSubmit: onSearchSubmit,
                    onChange: onSearchValueChange,
                    options: searchOptions,
                    defaultValue: searchCriteria,
                    handleChange: handleSearchCriteriaChange
                  }
                }
              }}
            />
          </Box>
        </ColorBox>
      </Box>
    ) : (
      <CircularProgress />
    )
  ) : (
    <CircularProgress />
  )
}
